import React from 'react';
import { useQuery } from 'react-query';
import { History, X } from 'lucide-react';
import { auditService } from '../../services/api';
import { ACTION_CLASSES, ACTION_LABELS, formatActor, formatAuditDate, formatValue } from './auditLabels';

const RecordHistoryDrawer = ({ entityType, entityId, title, onClose }) => {
  const { data, isLoading, error } = useQuery(
    ['audit-record', entityType, entityId],
    async () => {
      const res = await auditService.record(entityType, entityId);
      return res.data;
    },
    { enabled: Boolean(entityType && entityId) }
  );

  const items = data?.items || [];

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 p-4 backdrop-blur-[1px]" role="presentation">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="record-history-title"
        className="flex max-h-[90vh] w-full max-w-3xl flex-col overflow-hidden rounded-xl border border-gray-200 bg-white shadow-strong"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex shrink-0 items-start justify-between gap-4 border-b border-gray-100 bg-gradient-to-r from-primary-50/90 via-white to-white px-6 py-4">
          <div className="flex min-w-0 flex-1 items-start gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary-100 text-primary-800 ring-1 ring-primary-200/50">
              <History className="h-5 w-5" aria-hidden />
            </div>
            <div className="min-w-0 flex-1">
              <h2 id="record-history-title" className="text-lg font-semibold tracking-tight text-gray-900">
                {title || data?.entity_label || 'Histórico do registro'}
              </h2>
              <p className="mt-1 text-sm leading-relaxed text-gray-600">
                {data?.entity_type_label || entityType}
                {data?.deleted ? ' · excluído (lixeira)' : ''}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="shrink-0 rounded-lg p-2 text-gray-400 transition-colors hover:bg-white/80 hover:text-gray-600"
            aria-label="Fechar"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-6 py-5">
          {isLoading ? (
            <p className="text-sm text-gray-500">Carregando histórico…</p>
          ) : error ? (
            <p className="text-sm text-rose-600">Não foi possível carregar o histórico deste registro.</p>
          ) : items.length === 0 ? (
            <p className="text-sm text-gray-500">Ainda não há ações registradas para este item.</p>
          ) : (
            <ol className="space-y-4">
              {items.map((item) => (
                <li key={item.id} className="rounded-lg border border-gray-200 bg-white p-4">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ring-1 ${ACTION_CLASSES[item.action] || 'bg-gray-50 text-gray-700 ring-gray-200'}`}>
                        {ACTION_LABELS[item.action] || item.action}
                      </span>
                      <p className="mt-2 text-sm font-medium text-gray-900">{item.summary || ACTION_LABELS[item.action]}</p>
                      <p className="mt-1 text-xs text-gray-500">
                        {formatActor(item)}
                        {item.actor_role ? ` · ${item.actor_role}` : ''}
                        {item.actor_email ? ` · ${item.actor_email}` : ''}
                      </p>
                    </div>
                    <time className="text-xs text-gray-500">{formatAuditDate(item.occurred_at)}</time>
                  </div>

                  {item.changes && Object.keys(item.changes).length > 0 ? (
                    <div className="mt-3 overflow-x-auto">
                      <table className="min-w-full text-xs">
                        <thead>
                          <tr className="text-left text-gray-500">
                            <th className="py-1 pr-3 font-medium">Campo</th>
                            <th className="py-1 pr-3 font-medium">Antes</th>
                            <th className="py-1 font-medium">Depois</th>
                          </tr>
                        </thead>
                        <tbody>
                          {Object.entries(item.changes).map(([field, diff]) => (
                            <tr key={field} className="border-t border-gray-100 align-top">
                              <td className="py-1.5 pr-3 font-medium text-gray-700">{field}</td>
                              <td className="py-1.5 pr-3 text-rose-700">{formatValue(diff?.antes)}</td>
                              <td className="py-1.5 text-emerald-700">{formatValue(diff?.depois)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : null}

                  {(item.before_snapshot || item.after_snapshot) ? (
                    <details className="mt-3">
                      <summary className="cursor-pointer text-xs font-medium text-primary-700">Ver dados completos</summary>
                      <div className="mt-2 grid gap-3 md:grid-cols-2">
                        {item.before_snapshot ? (
                          <pre className="max-h-56 overflow-auto rounded-md bg-gray-50 p-2 text-[11px] text-gray-700">
                            {JSON.stringify(item.before_snapshot, null, 2)}
                          </pre>
                        ) : null}
                        {item.after_snapshot ? (
                          <pre className="max-h-56 overflow-auto rounded-md bg-gray-50 p-2 text-[11px] text-gray-700">
                            {JSON.stringify(item.after_snapshot, null, 2)}
                          </pre>
                        ) : null}
                      </div>
                    </details>
                  ) : null}
                </li>
              ))}
            </ol>
          )}
        </div>

        <div className="flex shrink-0 justify-end border-t border-gray-100/90 bg-gray-50/95 px-6 py-4">
          <button type="button" onClick={onClose} className="btn btn-outline">
            Fechar
          </button>
        </div>
      </div>
    </div>
  );
};

export default RecordHistoryDrawer;
